// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TextEditorStyle } from '@way/theme-host/shared/components/text-editor/text-editor.component';

export interface InfoLayout4Data {
  title?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  image?: {
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    scaletype?: 'fill' | 'fit' | 'stretch' | 'cover' | 'contain';
  };
  imageUrl?: string;
  backgroundColor?: string;
  titleStyle?: TextEditorStyle;
}

/**
 * Preview component for "Info Layout 4" — renders a layout with heading, paragraph, image and exposes button interaction events.
 * @component
 * @selector app-info-layout-4-preview
 * @example
 *   <app-info-layout-4-preview
 *     [data]="sampleData"
 *     [headingColor]="'#000'"
 *     [paragraphColor]="'#333'"
 *     [headingFont]="'Arial'"
 *     [bodyFont]="'Roboto'"
 *     [primaryColor]="'#ff0000'"
 *     [titleStyles]="{ fontWeight: '700' }"
 *     (buttonClick)="handleButtonClick($event)">
 *   </app-info-layout-4-preview>
 * @input {InfoLayout4Data | null} data - The data model for the layout (text, image, links). May be null.
 * @input {string | null} headingColor - Optional color for headings; applied to heading text when provided.
 * @input {string | null} paragraphColor - Optional color for paragraph/body text.
 * @input {string | null} headingFont - Optional font family for headings.
 * @input {string | null} bodyFont - Optional font family for body/paragraph text.
 * @input {string | null} primaryColor - Optional primary accent color used by the layout (buttons, highlights).
 * @input {any} titleStyles - Additional inline style overrides applied to the title (object of CSS properties).
 * @output {EventEmitter<{ link: string; event: Event }>} buttonClick - Emitted when an actionable button/link is clicked; payload includes the link string and the original DOM event.
 *
 * Key methods:
 *   - onButtonClick(link: string | undefined, event: Event): void
 *       Emits the buttonClick event only if a link is provided. Used to propagate user interactions to parent components.
 *   - getImageSrc(): string
 *       Returns the image source by preferring data.image.src, falling back to data.imageUrl, or an empty string if none present.
 *   - getImageAlt(): string
 *       Returns the image alt text from data.image.alt or a sensible default ('Car Wash') when absent.
 *   - getImageObjectFit(): string
 *       Maps the data.image.scaletype value to a corresponding CSS object-fit value ('fill', 'contain', 'cover', etc.).
 *
 * Lifecycle hooks:
 *   - This standalone preview component does not implement Angular lifecycle hooks explicitly (no ngOnInit/ngOnDestroy defined).
 */
@Component({
  selector: 'app-info-layout-4-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './info-layout-4.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./info-layout-4.component.scss'],
})
export class InfoLayout4PreviewComponent {
  @Input() data: InfoLayout4Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Wash';
  }

  /**
   * Return the appropriate CSS object-fit value for the component's image based on its scaletype.
   * @example
   * etImageObjectFit()
   * 'cover'
   * @returns {string} CSS object-fit value mapped from image scaletype ('fill', 'contain', or 'cover').
   */
  getImageObjectFit(): string {
    const scaletype = this.data?.image?.scaletype || 'cover';
    // Map API scaletype to CSS object-fit
    switch (scaletype) {
      case 'fill':
      case 'stretch':
        return 'fill';
      case 'fit':
        return 'contain';
      case 'cover':
        return 'cover';
      case 'contain':
        return 'contain';
      default:
        return 'cover';
    }
  }
}
